import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  List,
  ListItem,
  Divider,
  Avatar,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Send as SendIcon,
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import '../../styles/StudentWall.css';
import StudentWallNavbar from '../../components/StudentWallNavbar';
import axiosInstance from '../../utils/axiosInstance';
import config from '../../config.json';
import url from '../../url.json';
import io from 'socket.io-client';

const sortOptions = ['最新', '最舊', '精選優先', '最多按讚'];

export default function CrossGroupDiscussion() {
  const { courseDispatchId } = useParams(); // 跨組討論不需要 groupId，因為是整個課程共用
  const navigate = useNavigate();
  
  const [currentWall, setCurrentWall] = useState('cross_group');
  const [sortBy, setSortBy] = useState('最新');
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  
  // 用於防止重複點擊的 ref
  const isSubmittingRef = useRef(false);
  const [newComment, setNewComment] = useState({
    content: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [courseDispatch, setCourseDispatch] = useState(null);
  const [ws, setWs] = useState(null); // WebSocket 連接
  
  // 可用的報告小組列表
  const [availableGroups, setAvailableGroups] = useState([]);

  // 當前選擇的報告小組
  const [presentingGroup, setPresentingGroup] = useState(null);
  
  // 跨組討論留言資料
  const [comments, setComments] = useState([]);
  const [likedComments, setLikedComments] = useState(new Set()); // 記錄已按讚的留言 ID

  // 取得當前使用者 ID（用於持久化按讚狀態）
  const getCurrentUserId = () => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) return null;
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      const decoded = JSON.parse(jsonPayload);
      return decoded.dataValues?.id || decoded.id || decoded.userId || null;
    } catch {
      return null;
    }
  };

  // 載入課程派發資料和小組列表
  const fetchCourseDispatchAndGroups = async () => {
    try {
      setLoading(true);
      setError(null);

      // 嘗試獲取課程派發詳情（如果失敗也不影響，因為跨組討論主要需要小組列表）
      try {
        const dispatchResponse = await axiosInstance.get(`${config.getCourseDispatchDetail}${courseDispatchId}/detail`);
        if (dispatchResponse.data.success) {
          const detailData = dispatchResponse.data.data;
          const appInfo = detailData.applicationInfo || {};
          setCourseDispatch(appInfo);
        }
      } catch (err) {
        console.warn('無法獲取課程派發詳情（可能權限不足）:', err);
        // 如果無法獲取詳情，至少嘗試從 class 獲取基本信息
        // 這不會阻止跨組討論功能的使用
      }

      // 獲取課程小組列表（這是必需的）
      const groupsResponse = await axiosInstance.get(`${config.getCourseDispatchGroups}${courseDispatchId}/groups`);
      if (groupsResponse.data.success) {
        const groups = groupsResponse.data.data || [];
        setAvailableGroups(groups);
        
        // 設定預設報告小組（第一個小組）
        if (groups.length > 0 && !presentingGroup) {
          setPresentingGroup(groups[0]);
        }
      } else {
        setError('無法載入小組列表');
      }
    } catch (err) {
      console.error('載入課程資料失敗:', err);
      setError('載入課程資料失敗: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 載入跨組討論留言
  const fetchComments = async (reportingGroupId) => {
    try {
      setLoading(true);
      setError(null);

      const url = reportingGroupId 
        ? `${config.getCrossGroupDiscussion}${courseDispatchId}/${reportingGroupId}`
        : `${config.getCrossGroupDiscussion}${courseDispatchId}`;
      
      const response = await axiosInstance.get(url);
      if (response.data.success) {
        setComments(response.data.data || []);
      }
    } catch (err) {
      console.error('載入跨組討論留言失敗:', err);
      setError('載入留言失敗');
    } finally {
      setLoading(false);
    }
  };

  // 初始化載入資料
  useEffect(() => {
    if (courseDispatchId) {
      fetchCourseDispatchAndGroups();
    }
  }, [courseDispatchId]);

  // 載入已按讚列表（避免刷新後可重複按）
  useEffect(() => {
    const uid = getCurrentUserId();
    const key = `cross-group-likes:${uid || 'anon'}:${courseDispatchId}`;
    try {
      const saved = JSON.parse(localStorage.getItem(key) || '[]');
      if (Array.isArray(saved)) setLikedComments(new Set(saved));
    } catch {}
  }, [courseDispatchId]);

  // 當選擇的報告小組改變時，重新載入留言
  useEffect(() => {
    if (presentingGroup?.id) {
      fetchComments(presentingGroup.id);
    }
  }, [presentingGroup?.id]); // 只依賴 id，避免無限循環

  // 初始化 WebSocket 連接
  useEffect(() => {
    if (courseDispatchId) {
      console.log('[跨組討論] 初始化 WebSocket 連接，courseDispatchId:', courseDispatchId);
      console.log('[跨組討論] WebSocket 連接 URL:', url.socketioHost);
      console.log('[跨組討論] WebSocket 連接路徑:', '/s/socket.io');
      
      const socket = io.connect(url.socketioHost, { 
        path: '/s/socket.io',
        transports: ['websocket', 'polling'], // 明確指定傳輸方式
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
      });
      setWs(socket);

      socket.on('connect', () => {
        console.log('[跨組討論] WebSocket 已連接，socket.id:', socket.id);
      });

      socket.on('connect_error', (error) => {
        console.error('[跨組討論] WebSocket 連接錯誤:', error);
        console.error('[跨組討論] 連接失敗的 URL:', url.socketioHost);
      });

      socket.on('disconnect', (reason) => {
        console.log('[跨組討論] WebSocket 已斷開，原因:', reason);
      });

      // 清理函數
      return () => {
        console.log('[跨組討論] 斷開 WebSocket 連接');
        socket.disconnect();
      };
    }
  }, [courseDispatchId]);

  // WebSocket 即時更新監聽（跨組討論新留言）
  useEffect(() => {
    if (ws && courseDispatchId) {
      console.log('[跨組討論] 設置 WebSocket 監聽，courseDispatchId:', courseDispatchId);

      // 監聽跨組討論新留言事件
      const handleNewComment = (commentData) => {
        console.log('🔔 [跨組討論 WebSocket] ========== 收到新留言事件 ==========');
        console.log('  - 完整數據:', JSON.stringify(commentData, null, 2));
        console.log('  - 留言 ID:', commentData.id);
        console.log('  - 留言內容:', commentData.content);
        console.log('  - 報告小組 ID:', commentData.reporting_group_id);
        console.log('  - 課程派發 ID:', commentData.course_dispatch_id);

        // 檢查是否與當前選擇的報告小組相關，或者重新載入當前小組的留言
        if (commentData.course_dispatch_id == courseDispatchId) {
          console.log('✅ [跨組討論 WebSocket] 檢測到新留言，更新本地狀態');
          
          // 如果當前有選擇報告小組，且新留言屬於該小組，則更新留言列表
          if (presentingGroup?.id && commentData.reporting_group_id == presentingGroup.id) {
            console.log('  - 新留言屬於當前選擇的報告小組，更新留言列表');
            // 將新留言添加到本地狀態
            setComments(prev => {
              // 檢查是否已存在（避免重複）
              const exists = prev.some(c => c.id === commentData.id);
              if (exists) {
                console.log('  - 留言已存在，跳過添加');
                return prev;
              }
              console.log('  - 添加新留言到列表');
              return [commentData, ...prev];
            });
          } else if (presentingGroup?.id) {
            // 如果當前選擇的小組與新留言的小組不同，重新載入當前小組的留言（保持當前視角）
            console.log('  - 新留言屬於其他小組，保持當前視角不變');
          } else {
            // 如果沒有選擇報告小組，重新載入（但實際上應該已經有選擇）
            console.log('  - 當前未選擇報告小組');
          }
        } else {
          console.log('⚠️ [跨組討論 WebSocket] 忽略不同課程的留言');
        }
        console.log('========== 新留言事件處理結束 ==========');
      };

      // 監聽跨組討論專用事件
      ws.on(`cross-group-comment-${courseDispatchId}`, handleNewComment);

      // 也監聽通用的 node-recieve 事件（後端可能通過此事件廣播）
      const handleNewNode = (body) => {
        // 檢查是否為跨組討論類型的節點
        if (body.wall_type === 'cross_group' && body.course_dispatch_id == courseDispatchId) {
          console.log('🔔 [跨組討論 WebSocket] 透過 node-recieve 收到跨組討論新留言');
          handleNewComment(body);
        }
      };

      ws.on(`node-recieve-${courseDispatchId}`, handleNewNode);

      // 清理函數
      return () => {
        console.log('[跨組討論] 移除 WebSocket 監聽');
        ws.off(`cross-group-comment-${courseDispatchId}`, handleNewComment);
        ws.off(`node-recieve-${courseDispatchId}`, handleNewNode);
      };
    }
  }, [ws, courseDispatchId, presentingGroup?.id]);

  const handleWallChange = (wall) => {
    setCurrentWall(wall);
    // 獲取當前學生所在的小組ID（從 sessionStorage 或從 API 獲取）
    const activeGroupId = sessionStorage.getItem('groupId') || '1';
    if (wall === 'idea') {
      navigate(`/student/idea-wall/${courseDispatchId}/${activeGroupId}`);
    } else if (wall === 'question') {
      navigate(`/student/question-wall/${courseDispatchId}/${activeGroupId}`);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.content.trim() || !presentingGroup) {
      return;
    }

    // 防重複點擊：如果正在提交，直接返回
    if (isSubmittingRef.current) {
      console.log('[handleSubmitComment] 正在提交中，忽略重複點擊');
      return;
    }

    try {
      isSubmittingRef.current = true;
      setLoading(true);
      const response = await axiosInstance.post(config.createCrossGroupComment, {
        course_dispatch_id: courseDispatchId,
        reporting_group_id: presentingGroup.id,
        content: newComment.content
      });

      if (response.data.success) {
        // 重新載入留言列表
        await fetchComments(presentingGroup.id);
        setNewComment({ content: '' });
        setOpenCommentDialog(false);
      }
    } catch (err) {
      console.error('發表留言失敗:', err);
      setError('發表留言失敗');
    } finally {
      setLoading(false);
      // 延遲清除標記，避免快速重複點擊
      setTimeout(() => {
        isSubmittingRef.current = false;
      }, 1000);
    }
  };

  const handleLike = async (commentId, currentLikes) => {
    const isLiked = likedComments.has(commentId);
    const newIsLiked = !isLiked;

    try {
      const response = await axiosInstance.put(`${config.toggleLikeComment}${commentId}/like`, {
        isLiked: newIsLiked
      });

      if (response.data.success) {
        // 更新留言列表
        setComments(prev => prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, likes: response.data.data.likes }
            : comment
        ));

        // 更新按讚狀態
        setLikedComments(prev => {
          const uid = getCurrentUserId();
          const key = `cross-group-likes:${uid || 'anon'}:${courseDispatchId}`;
          const updated = new Set(prev);
          if (newIsLiked) updated.add(commentId); else updated.delete(commentId);
          try { localStorage.setItem(key, JSON.stringify(Array.from(updated))); } catch {}
          return updated;
        });
      }
    } catch (err) {
      console.error('按讚操作失敗:', err);
    }
  };

  // 排序留言
  const sortedComments = [...comments].sort((a, b) => {
    if (sortBy === '精選優先') {
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;
      // 如果都是精選或都不是，按時間排序
      return new Date(b.created_at) - new Date(a.created_at);
    }
    if (sortBy === '最新') {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    if (sortBy === '最舊') {
      return new Date(a.created_at) - new Date(b.created_at);
    }
    if (sortBy === '最多按讚') {
      return (b.likes || 0) - (a.likes || 0);
    }
    return 0;
  });

  return (
    <div className="teacher-layout class-application-question-wall-container">
      <div className="main-content-v2">
        <StudentWallNavbar 
          currentWall={currentWall}
          onWallChange={handleWallChange}
          courseDispatchId={courseDispatchId}
          groupId={sessionStorage.getItem('groupId')}
        />
        
        <div className="class-header-row">
          <h1 className="main-title">
            跨組討論
            {courseDispatch && (
              <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 1, display: 'inline' }}>
                - {courseDispatch.title || courseDispatch.activity_name || '課程'}
              </Typography>
            )}
          </h1>
        </div>

        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && !comments.length && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        <Grid container spacing={2} sx={{ height: 'calc(100vh - 200px)' }}>
          {/* 左側控制面板 */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', overflow: 'auto' }}>
              <CardContent>
                {/* 報告小組選擇 */}
                <Typography variant="h6" gutterBottom sx={{ color: '#1e4d5f', fontWeight: 'bold' }}>
                  🎤 報告小組
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>選擇報告小組</InputLabel>
                  <Select
                    value={presentingGroup?.id || ''}
                    onChange={(e) => {
                      const selected = availableGroups.find(g => g.id === e.target.value);
                      if (selected) {
                        setPresentingGroup({ id: selected.id, groupName: selected.groupName || selected.name });
                      }
                    }}
                    label="選擇報告小組"
                    disabled={loading || !availableGroups.length}
                  >
                    {availableGroups.map(group => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.groupName || group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Card sx={{ mb: 2, bgcolor: '#f0f8ff', border: '2px solid #1e4d5f' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GroupIcon sx={{ color: '#1e4d5f' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e4d5f' }}>
                          {presentingGroup?.groupName || '未選擇'}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  排序方式
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>排序方式</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label="排序方式"
                  >
                    {sortOptions.map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenCommentDialog(true)}
                  sx={{ mb: 2, backgroundColor: '#1e4d5f', '&:hover': { backgroundColor: '#163a4a' } }}
                >
                  發表留言
                </Button>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="text.secondary">
                  共 {sortedComments.length} 則留言
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* 右側留言列表 */}
          <Grid item xs={12} md={8}>
            <Card sx={{ height: '100%', overflow: 'auto' }}>
              <CardContent>
                {sortedComments.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      目前還沒有留言
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      點擊「發表留言」開始討論
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {sortedComments.map((comment, index) => (
                      <React.Fragment key={comment.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          sx={{
                            py: 0.5,
                            px: 1,
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
                            <Avatar sx={{ bgcolor: '#2b687f', width: 32, height: 32 }}>
                              {(comment.author_name || comment.Author?.name || '?').charAt(0)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  {comment.is_featured && (
                                    <Chip 
                                      label="精選" 
                                      size="small" 
                                      color="warning"
                                      sx={{ height: 18, fontSize: '0.7rem' }} 
                                    />
                                  )}
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                    {comment.author_name || comment.Author?.name || '未知'}
                                  </Typography>
                                  <Chip 
                                    label={comment.author_group_name || comment.Group?.groupName || '未知組別'} 
                                    size="small" 
                                    variant="outlined" 
                                    sx={{ height: 18, fontSize: '0.7rem' }} 
                                  />
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  {new Date(comment.created_at).toLocaleString('zh-TW')}
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem', lineHeight: 1.4 }}>
                                {comment.content}
                              </Typography>
                              <Button
                                size="small"
                                startIcon={<ThumbUpIcon />}
                                onClick={() => handleLike(comment.id, comment.likes)}
                                color={likedComments.has(comment.id) ? 'primary' : 'inherit'}
                                sx={{ minWidth: 60, fontSize: '0.75rem', py: 0.5 }}
                              >
                                {comment.likes || 0}
                              </Button>
                            </Box>
                          </Box>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 發表留言對話框 */}
        <Dialog open={openCommentDialog} onClose={() => setOpenCommentDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>發表留言</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Card sx={{ bgcolor: '#f0f8ff', border: '2px solid #1e4d5f' }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e4d5f', mb: 1 }}>
                    🎤 正在報告：{presentingGroup?.groupName || '未選擇'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    針對此報告發表您的想法、提問或建議
                  </Typography>
                </CardContent>
              </Card>
              
              <TextField
                label="留言內容"
                value={newComment.content}
                onChange={(e) => setNewComment({ content: e.target.value })}
                multiline
                rows={6}
                fullWidth
                placeholder="請輸入您的想法、問題或建議..."
                autoFocus
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
            <Button onClick={() => setOpenCommentDialog(false)}>取消</Button>
            <Button 
              onClick={handleSubmitComment} 
              variant="contained"
              disabled={!newComment.content.trim()}
            >
              發表留言
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}
